import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useClientAuth } from '../../auth/ClientAuthContext';
import API_BASE from '../../utils/api';
import { documentLabel } from '../../utils/documentTitle';
import { PO_STATUS_ORDER, isStepReached, poBadgeClass } from './tabs/badgeUtils';

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

export default function ClientPurchaseOrderDetail() {
  const { projectId, poId } = useParams();
  const { clientAuth } = useClientAuth();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clientAuth?.token) return;

    fetch(`${API_BASE}/api/client/projects/${projectId}/purchase-orders/${poId}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${clientAuth.token}` },
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
        return data;
      })
      .then(setPo)
      .catch((err) => setError(err.message || 'Failed to load purchase order.'))
      .finally(() => setLoading(false));
  }, [clientAuth, projectId, poId]);

  if (loading) return <p style={{ color: '#6b7280', fontSize: 14 }}>Loading…</p>;
  if (error) return (
    <>
      <Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link>
      <p className="cp-error">{error}</p>
    </>
  );
  if (!po) return null;

  const items = Array.isArray(po.items) ? po.items : [];
  const hasImages = items.some((it) => it.imageUrl);

  return (
    <div className="cp-animate">
      <Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link>

      <div className="cp-card">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              {documentLabel(po.orderNumber, po.title) || 'Purchase Order'}
            </h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 6 }}>
              {po.vendorName && (
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Vendor: {po.vendorName}</p>
              )}
              {po.date && (
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Date: {formatDate(po.date)}</p>
              )}
              {po.sidemark && (
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Sidemark: {po.sidemark}</p>
              )}
            </div>
          </div>
          <POStatusBadge status={po.status} />
        </div>

        {po.notes && (
          <p style={{ fontSize: 13, color: '#374151', marginBottom: 20, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {po.notes}
          </p>
        )}

        {Array.isArray(po.attachments) && po.attachments.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Attachments</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {po.attachments.map((a) => (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                  {a.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Items */}
        {items.length > 0 ? (
          <div className="cp-po-items">
            {items.map((item, i) => (
              <div key={item.itemId || i} className="cp-po-item-row">
                {hasImages && (
                  <div className="cp-po-item-img">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" />
                    ) : (
                      <div className="cp-po-item-img-placeholder" />
                    )}
                  </div>
                )}
                <div className="cp-po-item-info">
                  <div className="cp-po-item-name">{item.name || '—'}</div>
                  {item.description && (
                    <div className="cp-po-item-desc">{item.description}</div>
                  )}
                  <div className="cp-po-item-meta">
                    {item.room && <span>Room: {item.room}</span>}
                    {item.quantity != null && <span>Qty: {item.quantity}</span>}
                    {item.unitType && item.unitType !== 'Each' && <span>{item.unitType}</span>}
                  </div>
                </div>
                <div className="cp-po-item-status">
                  <ItemStatusBadge status={item.status} />
                  <div className="cp-po-status-track">
                    {PO_STATUS_ORDER.map((step) => (
                      <div
                        key={step}
                        className={`cp-po-status-step ${isStepReached(item.status, step) ? 'reached' : ''} ${item.status === step ? 'active' : ''}`}
                        title={step}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="cp-empty">No items on this order.</p>
        )}
      </div>
    </div>
  );
}

function POStatusBadge({ status }) {
  return <span className={`cp-badge ${poBadgeClass(status)}`}>{status || 'No status'}</span>;
}

function ItemStatusBadge({ status }) {
  return <span className={`cp-badge ${poBadgeClass(status)}`}>{status || 'No status'}</span>;
}
